import { useCallback, useEffect, useState } from "react";
import livechatApi from "./livechatApi";
import Global from "../../customlib/Global";

// DEV-1234 — Level Akses Live Chat Customer (GET /livechat/agent/me). Privilege hanya berlaku di
// brand customer; corporate & mitra-purchasing tidak diatur level (lihat canForSession).
export const LIVECHAT_PERMISSION = {
    SESSION_REPLY: "session.reply",
    SESSION_LABEL: "session.label",
    SESSION_CLOSE: "session.close",
    SESSION_GENERATE_LINK: "session.generateLink",
    SESSION_ORDER_INFO: "session.orderInfo",
    SESSION_CLAIM: "session.claim",
    AUTOLEADS_JOIN: "autoleads.join",
    AUTOLEADS_VIEW_AGENTS: "autoleads.viewAgents",
    AUTOLEADS_MANAGE: "autoleads.manage",
    INBOX_FILTER_BY_AGENT: "inbox.filterByAgent",
    BRAND_SETTINGS: "brand.settings",
    // MTN-956 — Tim CS: pantau semua chat Selesai & Alihkan Chat.
    CLOSED_VIEW_ALL: "closed.viewAll",
    SESSION_TRANSFER: "session.transfer",
};

const NON_CUSTOMER_BRANDS = ["corporate", "mitra-purchasing"];
export const isCustomerBrand = (brand) => !!brand && !NON_CUSTOMER_BRANDS.includes(brand);

const P = LIVECHAT_PERMISSION;

// Sebelum /me termuat atau kalau backend belum punya endpoint ini: perilaku sebelum Level Akses
// (semua agent boleh aksi percakapan; Kelola Sales & Settings dari flag admin login).
const legacyAccess = () => ({
    loaded: false,
    levelKey: "",
    levelName: "",
    assignmentScope: "unassigned",
    ownerScope: "mine_unowned",
    permissions: [
        P.SESSION_REPLY, P.SESSION_LABEL, P.SESSION_CLOSE, P.SESSION_GENERATE_LINK, P.SESSION_ORDER_INFO, P.SESSION_CLAIM, P.AUTOLEADS_JOIN,
        ...(Global.get()?.admin ? [P.AUTOLEADS_VIEW_AGENTS, P.AUTOLEADS_MANAGE, P.BRAND_SETTINGS] : []),
    ],
    canManageAccess: false,
});

/**
 * `onChanged` dipanggil setelah admin mengubah Level Akses (event socket `access:changed`) dan hak
 * akses dimuat ulang — index.jsx menyambung ulang socket karena room inbox ditentukan saat connect.
 */
export default function useLivechatAccess(socket, onChanged) {
    const [access, setAccess] = useState(legacyAccess);

    const reload = useCallback(async () => {
        try {
            const { data } = await livechatApi.getMyAccess();
            const d = data?.data || {};
            setAccess({
                loaded: true,
                levelKey: d.level?.key || "",
                levelName: d.level?.name || "",
                assignmentScope: d.assignmentScope || "none",
                ownerScope: d.ownerScope || "mine",
                permissions: Array.isArray(d.permissions) ? d.permissions : [],
                canManageAccess: !!d.canManageAccess,
            });
        } catch (err) {
            console.error("Error in useLivechatAccess.reload:", err);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (!socket) return undefined;
        const handleChanged = async () => {
            await reload();
            onChanged?.();
        };
        socket.on("access:changed", handleChanged);
        return () => socket.off("access:changed", handleChanged);
    }, [socket, reload, onChanged]);

    const can = useCallback((permission) => access.permissions.includes(permission), [access.permissions]);

    /** Privilege untuk aksi pada 1 sesi: corporate & mitra tidak diatur Level Akses (selalu boleh). */
    const canForSession = useCallback(
        (session, permission) => !session || !isCustomerBrand(session.brand) || access.permissions.includes(permission),
        [access.permissions]
    );

    return { access, can, canForSession, reload };
}
